import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DateTime } from 'luxon';
import { EntranceGroup } from '../entrance-groups/entrance-groups.component';
import {
  EntranceRate,
  ErrorAggregate,
  applyEntranceGroups,
  computeEntranceRates,
  computeErrorAggregates,
  computeScanRateTotals,
  entranceGroupsStorageKey,
  entranceNames as summaryEntranceNames,
  formatErrorCode,
  groupingOnStorageKey,
} from '../../../../services/attendance-metrics';

/**
 * Presentational "modern view" of an attendance summary: the hero tile, stat
 * tiles, scan-rate-by-entrance grid and error-by-result-code breakdown.
 *
 * Shared between the authenticated attendance page and the public share page so
 * both render an identical look from the same source of truth. The component is
 * purely presentational — it derives its figures from the `attendanceSummary`
 * input and raises outputs for every action rather than performing them itself.
 */
@Component({
  selector: 'app-modern-summary',
  standalone: false,
  templateUrl: './modern-summary.component.html',
  styleUrls: ['./modern-summary.component.scss', '../../styles/shared.scss'],
})
export class ModernSummaryComponent implements OnChanges {
  /** Raw attendance summary; scan metrics are recomputed whenever it changes. */
  @Input() attendanceSummary: any = {};
  @Input() venueName: string = '';
  @Input() date: string = '';
  @Input() endDate: string = '';
  @Input() dateRange: boolean = false;
  @Input() liveDate: string = '';
  @Input() venueTimezone: string = '';
  @Input() useDarkMode: boolean = false;
  @Input() stopDataToggle: boolean = false;
  @Input() liveButton: boolean = false;
  @Input() fireworkIntensity: number = 0;
  @Input() fireworkLabel: string = '';

  /** Toggles for the optional hero action buttons. */
  @Input() showShare: boolean = true;
  @Input() showEmail: boolean = true;
  @Input() showSchedule: boolean = true;
  /** Whether to render the "Errors by Result Code" breakdown (Ticketmaster-only on the attendance page). */
  @Input() showErrorBreakdown: boolean = true;

  /**
   * Enables the entrance-grouping UI (icon, modal, toggle). Off by default so the
   * public share page — which reuses this component — stays read-only.
   */
  @Input() allowEntranceGrouping: boolean = false;
  /** Venue key the groups belong to; scopes the per-venue localStorage entries. */
  @Input() venueKey: string = '';

  @Output() download = new EventEmitter<void>();
  @Output() share = new EventEmitter<void>();
  @Output() email = new EventEmitter<void>();
  @Output() schedule = new EventEmitter<void>();
  @Output() pause = new EventEmitter<void>();
  @Output() play = new EventEmitter<void>();
  @Output() darkModeToggle = new EventEmitter<boolean>();
  /** Fires when the user asks to open the full-screen big-screen dashboard. */
  @Output() openBigScreen = new EventEmitter<void>();

  totalScanRate: number = 0;
  totalSuccessRate: number = 0;
  totalErrorRate: number = 0;
  topEntrancesByRate: EntranceRate[] = [];
  /**
   * The entrance tiles actually rendered. Held as a field (not a getter) and
   * recomputed only when its inputs change, so `*ngFor` keeps stable references
   * and doesn't rebuild every tile — and restart its counters — each change
   * detection cycle. Equals {@link topEntrancesByRate} unless grouping is on.
   */
  displayEntrances: EntranceRate[] = [];
  errorAggregates: ErrorAggregate[] = [];
  selectedErrorAggregate: ErrorAggregate | null = null;

  /** localStorage key persisting the "Fans Inside as headline" preference. */
  private static readonly FANS_INSIDE_PRIMARY_KEY = 'attendanceFansInsidePrimary';

  /** Saved entrance groups for the current venue (drives the summed view). */
  entranceGroups: EntranceGroup[] = [];
  /** Whether grouped entrances are currently collapsed into summed tiles. */
  groupingOn: boolean = false;
  /** Whether the manage-groups modal is open. */
  entranceGroupsModalOpen: boolean = false;
  /**
   * Every entrance key for the venue, fed to the manage-groups modal. Held as a
   * field (recomputed when the summary changes) rather than a getter so the
   * child's `@Input` keeps a stable reference — a fresh array each change
   * detection cycle would re-fire its `ngOnChanges` and discard in-progress edits
   * (e.g. a just-added empty group).
   */
  allEntranceNames: string[] = [];

  /**
   * When true, "Fans Inside" is promoted to the hero headline and the Entries
   * total drops into the secondary stat tile. Persisted so the choice survives
   * reloads. Defaults to false (Entries remains the headline).
   */
  fansInsidePrimary: boolean = false;

  constructor() {
    try {
      this.fansInsidePrimary =
        localStorage.getItem(ModernSummaryComponent.FANS_INSIDE_PRIMARY_KEY) === 'true';
    } catch {
      // localStorage can be unavailable (private mode / SSR); keep the default.
      this.fansInsidePrimary = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attendanceSummary']) {
      this.computeScanMetrics();
    }
    // Reload the saved groups whenever the venue changes (they're per-venue).
    if (changes['venueKey']) {
      this.loadEntranceGroups();
    }
  }

  /** Swaps which metric is shown as the hero headline vs the secondary tile. */
  toggleFansInsidePrimary(): void {
    this.fansInsidePrimary = !this.fansInsidePrimary;
    try {
      localStorage.setItem(
        ModernSummaryComponent.FANS_INSIDE_PRIMARY_KEY,
        String(this.fansInsidePrimary),
      );
    } catch {
      // Persisting is best-effort; the in-memory toggle still applies.
    }
  }

  /**
   * Scan rates are only meaningful when the venue is actively scanning. When the
   * headline total drops to zero, the view suppresses /min figures and lets the
   * occupancy counts (fans inside) take precedence instead.
   */
  get hasScanRate(): boolean {
    return this.totalScanRate > 0;
  }

  /** Whether any groups exist — gates the group toggle and the summed view. */
  get hasEntranceGroups(): boolean {
    return this.entranceGroups.length > 0;
  }

  /**
   * Recomputes {@link displayEntrances}. With grouping off (or no groups defined)
   * it's the plain per-entrance list. With grouping on, members of each group are
   * summed into one synthetic tile named after the group, and any entrance left
   * in no group still shows individually. The same "has activity" filter and
   * busiest-first sort as {@link computeScanMetrics} are applied so the grid
   * stays consistent.
   */
  private computeDisplayEntrances(): void {
    this.displayEntrances = applyEntranceGroups(
      this.topEntrancesByRate,
      this.entranceGroups,
      this.groupingOn && this.hasEntranceGroups
    );
  }

  openEntranceGroups(): void {
    this.entranceGroupsModalOpen = true;
  }

  closeEntranceGroups(): void {
    this.entranceGroupsModalOpen = false;
  }

  /** Persists the edited groups, closes the modal, and refreshes the view. */
  onEntranceGroupsSaved(groups: EntranceGroup[]): void {
    this.entranceGroups = groups;
    this.saveEntranceGroups();
    // If every group was removed, drop back to the ungrouped view.
    if (!this.hasEntranceGroups) {
      this.groupingOn = false;
      this.saveGroupingOn();
    }
    this.entranceGroupsModalOpen = false;
    this.computeDisplayEntrances();
  }

  toggleGrouping(): void {
    this.groupingOn = !this.groupingOn;
    this.saveGroupingOn();
    this.computeDisplayEntrances();
  }

  /** Loads this venue's saved groups and toggle state; resets when none exist. */
  private loadEntranceGroups(): void {
    if (!this.venueKey) {
      this.entranceGroups = [];
      this.groupingOn = false;
      return;
    }
    try {
      const raw = localStorage.getItem(this.groupsStorageKey());
      const parsed = raw ? JSON.parse(raw) : [];
      this.entranceGroups = Array.isArray(parsed) ? parsed : [];
    } catch {
      // Corrupt or unavailable storage: fall back to no groups.
      this.entranceGroups = [];
    }
    try {
      this.groupingOn =
        this.hasEntranceGroups &&
        localStorage.getItem(this.groupingStorageKey()) === 'true';
    } catch {
      this.groupingOn = false;
    }
    this.computeDisplayEntrances();
  }

  private saveEntranceGroups(): void {
    if (!this.venueKey) return;
    try {
      localStorage.setItem(this.groupsStorageKey(), JSON.stringify(this.entranceGroups));
    } catch {
      // Persisting is best-effort; the in-memory groups still apply.
    }
  }

  private saveGroupingOn(): void {
    if (!this.venueKey) return;
    try {
      localStorage.setItem(this.groupingStorageKey(), String(this.groupingOn));
    } catch {
      // Best-effort; the in-memory toggle still applies.
    }
  }

  private groupsStorageKey(): string {
    return entranceGroupsStorageKey(this.venueKey);
  }

  private groupingStorageKey(): string {
    return groupingOnStorageKey(this.venueKey);
  }

  onDarkModeToggle(event: any): void {
    this.darkModeToggle.emit(!!event?.target?.checked);
  }

  onOpenBigScreen(): void {
    this.openBigScreen.emit();
  }

  openErrorDetail(err: ErrorAggregate): void {
    this.selectedErrorAggregate = err;
  }

  closeErrorDetail(): void {
    this.selectedErrorAggregate = null;
  }

  formatErrorCode(code: string): string {
    return formatErrorCode(code);
  }

  formatHeroDate(dateStr: string): string {
    if (!dateStr) return '';
    const zone = this.venueTimezone || 'UTC';
    const dt = DateTime.fromISO(dateStr, { zone });
    if (!dt.isValid) return dateStr;
    const day = dt.day;
    const suffix =
      day % 10 === 1 && day !== 11 ? 'st'
      : day % 10 === 2 && day !== 12 ? 'nd'
      : day % 10 === 3 && day !== 13 ? 'rd'
      : 'th';
    return `${dt.toFormat('ccc')} ${day}${suffix} ${dt.toFormat('LLLL yyyy')}`;
  }

  private computeScanMetrics(): void {
    const summary = this.attendanceSummary || {};

    const entranceKeys = summaryEntranceNames(summary);
    // Only swap the array when the key set actually changes, so a live payload
    // doesn't hand the modal a fresh reference (which would reset its edits).
    if (
      entranceKeys.length !== this.allEntranceNames.length ||
      entranceKeys.some((k, i) => k !== this.allEntranceNames[i])
    ) {
      this.allEntranceNames = entranceKeys;
    }

    this.topEntrancesByRate = computeEntranceRates(summary);

    const totals = computeScanRateTotals(summary);
    this.totalSuccessRate = totals.totalSuccessRate;
    this.totalErrorRate = totals.totalErrorRate;
    this.totalScanRate = totals.totalScanRate;

    this.errorAggregates = computeErrorAggregates(summary);

    if (this.selectedErrorAggregate) {
      const refreshed = this.errorAggregates.find((e) => e.code === this.selectedErrorAggregate!.code);
      this.selectedErrorAggregate = refreshed || null;
    }

    // Keep the rendered tiles (grouped or not) in step with the fresh figures.
    this.computeDisplayEntrances();
  }
}
